import { ActionMenuPopover, ActionsMenuOption } from '@fox/atoms/src/action-menu/index';
import { Button } from '@fox/atoms/src/button/index';
import { Divider } from '@fox/atoms/src/divider/index';
import { useFullScreenContext } from '@fox/atoms/src/full-screen/index';
import { Icon } from '@fox/atoms/src/icon/index';
import { motion, useAnimation } from 'framer-motion';
import React, { forwardRef } from 'react';
import { useAutoDataGridContext } from '../auto-data-grid/context';
import { GraphColumn } from '../auto-data-grid/types';
import { ColumnFilter, ToolbarFilter } from './filter';
import { GroupByPopover } from './group-by/group-by-popover';
import { HidePopover } from './hide';
import { PinPopover } from './pin';
import { SearchBar } from './search';
import { SortPopover } from './sort';

import styled from 'styled-components';
import { DefaultToolbarItems, ToolbarItems } from './items';
import { ResetLocalView } from './reset-local-view';

export * from './checklist';
export * from './filter';
export * from './hide';
export * from './items';
export * from './pin';
export * from './search';
export * from './sort';

const ToolbarConextMenu = ({ buildToolbarActionMenu }) => {
	if (!buildToolbarActionMenu) return <></>;
	const toolbarMenuOptions = buildToolbarActionMenu();
	return <ActionMenuPopover options={toolbarMenuOptions} />;
};

const FullScreen = styled.div`
	margin: 1rem 0 1rem auto;
	display: flex;
	align-items: center;
	.rowAmount {
		opacity: 0.8;
		font-size: 0.8rem;
		margin-right: 0.5rem;
	}
`;

//we might not need this?
export const LabelSelectToolbarWrapper = styled.div`
	width: 25rem;
	.label-input-wrapper {
		margin-top: 0;
	}
	div {
		padding-top: 0 !important;
	}
`;

/**
 *  The toolbar for the AutoGrid
 */
export const Toolbar = forwardRef<
	HTMLDivElement,
	{
		data: unknown[];
		columns: (GraphColumn<unknown> | ColumnFilter)[];
		buildToolbarActionMenu: () => ActionsMenuOption[];
		total?: number;
		items?: ToolbarItems[];
		paddedToolbar?: boolean;
	}
>((props, ref) => {
	const { pinnedColumnsDataIndex, search, id, localSaveOnly } = useAutoDataGridContext();
	const { fullScreen, setFullScreen } = useFullScreenContext();
	const { data, columns, buildToolbarActionMenu } = props;

	const items = props.items ?? [];

	// disable pin and hide if there is only one column
	// const itemsFiltered = columns.length === 1 ? items.filter((e: ToolbarItems) => e !== 'Pin' && e !== 'Hide') : items;

	// TODO: get numHidden and hide HidePopover and PinPopover if 0
	const numPinned = pinnedColumnsDataIndex.length;
	const sortableColumns = columns.filter((a): a is GraphColumn<unknown> => 'sortable' in a && typeof a.sortable !== 'undefined');
	const groupByAbleColumns = columns.filter((c): c is GraphColumn<unknown> => 'groupByFunction' in c && c.groupByFunction !== undefined);

	const itemDict: Record<DefaultToolbarItems, JSX.Element | null> = {
		Filter: columns.map(c => c.filterType !== undefined).every(el => el === false) ? null : <ToolbarFilter columns={columns} />,
		Sort: sortableColumns.length > 0 ? <SortPopover options={sortableColumns} /> : null,
		Search: search === undefined ? null : <SearchBar onExpand={animateExpand} onShrink={animateShrink} />,
		Pin: Boolean(numPinned + 1) ? <PinPopover /> : null,
		Divider: <ToolbarDivider />,
		Hide: <HidePopover />,
		GroupBy: groupByAbleColumns?.length >= 1 ? <GroupByPopover columns={groupByAbleColumns} /> : null,
		ContextMenu: buildToolbarActionMenu ? <ToolbarConextMenu buildToolbarActionMenu={buildToolbarActionMenu} /> : null,
		ResetLocalView: localSaveOnly ? <ResetLocalView /> : null
	};
	// bisect the toolbar into two slices, [..., Search, ...] becomes [..., Search] and [...]
	// so that the latter half can be isolated in animation
	const searchIdx = items.indexOf('Search');
	const searchAndBefore = items.slice(0, searchIdx + 1);
	const afterSearch = items.slice(searchIdx + 1);

	// Animation for sliding the toolbar (right slice) when search is opened and closed
	const controls = useAnimation();
	async function animateExpand() {
		await controls.start(() => ({ x: -200, transition: { duration: 0.001 } }));
		await controls.start(() => ({ x: 0, transition: { duration: 0.5 } }));
	}
	async function animateShrink() {
		await controls.start(() => ({ x: 200, transition: { duration: 0.001 } }));
		await controls.start(() => ({ x: 0, transition: { duration: 0.5 } }));
	}

	const total = props.total ?? data?.length;

	return (
		<div ref={ref} style={{ display: 'flex', width: '100%', paddingLeft: props.paddedToolbar ? '1rem' : '0' }}>
			<div
				style={{
					display: 'flex',
					flexDirection: 'row',
					justifyItems: 'center',
					gap: '1rem',
					alignItems: 'center',
					margin: 0,
					flexWrap: 'wrap',
					width: '100%'
				}}
			>
				{searchAndBefore.map((x, idx) => (
					<React.Fragment key={`left-${idx}`}>{React.isValidElement(x) ? x : itemDict[x as DefaultToolbarItems]}</React.Fragment>
				))}
				{afterSearch.map((x, idx) => {
					const element = React.isValidElement(x) ? x : itemDict[x as DefaultToolbarItems];

					//make sure we only show the divider if there are items following it
					if (
						!element ||
						(x === 'Divider' &&
							(idx === afterSearch.length - 1 ||
								afterSearch
									.slice(idx)
									.every(
										x =>
											React.isValidElement(x) === false ||
											(typeof x === 'string' && itemDict[x as DefaultToolbarItems] === null)
									)))
					)
						return null;
					// this motion.div is required so the items to the right of search animate as it grows
					return (
						<motion.div key={`right-${idx}`} initial={false} animate={controls}>
							{element}
						</motion.div>
					);
				})}

				<FullScreen style={{ marginLeft: 'auto' }}>
					<div className="rowAmount">
						{total > 0 && (
							<div style={{ display: 'flex', gap: '5px' }}>
								<div>{total}</div>
								<div> Rows</div>
							</div>
						)}
					</div>
					{document.fullscreenElement !== undefined ? (
						<Button
							style={{ minWidth: 0, borderRadius: '35%/45%' }}
							title="Toggle Full Screen"
							onClick={() => setFullScreen(!fullScreen)}
						>
							<Icon iconName={fullScreen ? 'CloseFullscreen' : 'Fullscreen'} fontSize="small" />
						</Button>
					) : null}
				</FullScreen>
			</div>
		</div>
	);
});
export function ToolbarDivider(): JSX.Element {
	return <Divider style={{ height: '2.5rem' }} orientation="vertical" />;
}
